class DashboardWidget implements ITabOrWidget {
    tabOrWidget = TabOrWidgetEnum.Widget;

    widget: IWidget;
    widgetContent: any;
    dashboard: Dashboard;

    /**Used only to bind at the settings tab*/
    itemConfig: KnockoutObservable<BaseItemConfig> = ko.observable(null);
    /** itemConfig converted to ReportConfig */
    get reportConfig() { return this.itemConfig() instanceof ReportConfig ? this.itemConfig() as ReportConfig : null; }

    id: KnockoutObservable<number>;
    name: KnockoutObservable<string>;
    order: KnockoutObservable<number>;
    widgetType: KnockoutObservable<WidgetType>;
    manualLayout: LayoutConfig;
    filters: KnockoutObservableArray<Filter>;
    appearance: AppearanceConfig;
    tabDetailId: KnockoutObservable<number>;
    copyMetricsToTab: KnockoutObservable<boolean>;
    copyDimensionsToTab: KnockoutObservable<boolean>;

    private _expanded: boolean = false;
    get expanded() {
        return this._expanded;
    }

    get expandEnabled() {
        return this.widgetType() != WidgetType.Filter && this.widgetType() != WidgetType.KPI;
    }
    get isSelected() {
        return this.dashboard.selectedWidget()?.id() === this.id();
    }

    drilldownManager: DrilldownManager;
    private dashboarWidgetElement: HTMLElement;

    constructor(data: any, dashboard: Dashboard) {
        this.dashboard = dashboard;
        this.id = ko.observable(data.id);
        this.name = ko.observable(data.name);
        this.order = ko.observable(data.order);
        this.widgetType = ko.observable(data.widgetType);
        this.manualLayout = new LayoutConfig(data.manualLayout);
        this.appearance = new AppearanceConfig(data.appearance, dashboard);
        this.tabDetailId = ko.observable(data.tabDetailId);
        this.copyMetricsToTab = ko.observable(data.copyMetricsToTab);
        this.copyDimensionsToTab = ko.observable(data.copyDimensionsToTab);

        let filtersVM = createInstances(Filter, data.filters as Array<any>);
        this.filters = ko.observableArray(filtersVM);

        this.drilldownManager = new DrilldownManager(data.drilldownId, data.drilldownList, dashboard.tab, this);
        this.setupSettings();
    }

    getBaseSubmitObject() {
        return new BaseSubmitObjectForWorkbook(this.dashboard.tab, this);
    }

    setup() {
    }

    async refresh() {
        BaseWidget.refreshWidget(this);
    }

    print() {
        this.drilldownManager.getActiveWidget().print();
    }

    exportToPDF(isLandscape: boolean, templateId: number) {
        this.drilldownManager.getActiveWidget().exportToPDF(isLandscape, templateId);
    }

    exportToXLSX(withoutHeaders: boolean) {
        this.drilldownManager.getActiveWidget().exportToXLSX(withoutHeaders);
    }

    exportToCSV(withoutHeaders: boolean) {
        this.drilldownManager.getActiveWidget().exportToCSV(withoutHeaders);
    }

    async getWidgetData(callback: (result: any, error: boolean, errorMessage: string) => void, resetDrilldown?: boolean) {
        let self = this;
        let submitObject = this.dashboard.tab.getBaseSubmitObject();
        submitObject.widgetId = this.id();
        submitObject.widgetType = self.drilldownManager.getActiveWidget().widgetType;

        let o = {
            url: urlConfigs.dashboard + "UpdateWidgetReportData",
            data: submitObject,
            dataType: "json",
            async: true,
            showIndicator: false,

        } as RequestServerParams;
        o.successFunction = function (data, e) {
            if (data.filters) {
                let filtersVM = createInstances(Filter, data.filters as Array<any>);
                self.filters(filtersVM);
            }

            callback(data, false, "");
        }
        o.failureFunction = function (textStatus, failureText, errorItem: IError) {
            errorItem.userMessage = `Failed to get the widget`;
            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, failureText);
            callback({ error: failureText }, true, failureText);
        };

        irGlobal.requestServer(o);
    }

    setdashboarWidgetElement(dashboarWidgetElement: HTMLElement) {
        this.dashboarWidgetElement = dashboarWidgetElement;
    }

    private moveable: any; // Moveable; import Moveable from "moveable";

    async enableDisableResizableDraggable(enable: boolean) {
        if (this.moveable) {
            this.moveable.draggable = enable;
            this.moveable.resizable = enable;
        }
        else {
            if (enable) {
                this.configureResizableDraggable();
            }
        }
    }

    async updateMoveableRect() {
        if (this.moveable) {
            this.moveable.updateRect();
        }
    }

    removeResizableDraggable() {
        if (this.moveable) {
            this.moveable.destroy();
            this.moveable = null;
        }
    }

    remove() {
        let self = this;
        let submitObject = this.dashboard.tab.getBaseSubmitObject();
        submitObject.widgetId = this.id();

        let o = {
            url: urlConfigs.dashboard + "RemoveWidget",
            data: submitObject,
            dataType: "json"
        } as RequestServerParams;
        o.successFunction = function (data, e) {
            self.dashboard.selectWidget(null);
            self.dashboard.widgets.remove(self);

            if (self.widgetType() === WidgetType.Filter) {
                workbook.workbookConfig.filterHelper.updateFilters();
            }
        };
        o.failureFunction = function (textStatus, failureText, errorItem: IError) {
            errorItem.userMessage = `Failed to remove the widget`;
            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, failureText);
        };

        irGlobal.requestServer(o);
    }

    duplicate() {
        let self = this;
        let submitObject = this.dashboard.tab.getBaseSubmitObject() as any;
        submitObject.widgetId = this.id();

        let o = {
            url: urlConfigs.dashboard + "CopyWidget",
            data: submitObject,
            dataType: "json"
        } as RequestServerParams;
        o.successFunction = function (data, e) {
            let widget = new DashboardWidget(data.widget, self.dashboard);
            self.dashboard.widgets.push(widget);
            setTimeout(function () {
                self.dashboard.selectWidget(widget);
                widget.configureResizableDraggable();
            }, 300);
        };
        o.failureFunction = function (textStatus, failureText, errorItem: IError) {
            errorItem.userMessage = `Failed to duplicate widget`;
            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, failureText);
        };

        irGlobal.requestServer(o);
    }

    createAsNewTab() {
        if (this.widgetType() !== WidgetType.Report && this.widgetType() !== WidgetType.Pivot && this.widgetType() !== WidgetType.Chart) {
            return;
        }

        workbook.workbookConfig.workbookHelper.addTab(TabTypeEnum.Report, new ReportStub({ id: this.dashboard.tab.id(), name: this.name() }), this.id())
    }

    expandCollapse() {
        this._expanded = !this.expanded;
        if (this._expanded) {
            $(this.dashboarWidgetElement).addClass("expanded");
        }
        else {
            $(this.dashboarWidgetElement).removeClass("expanded");
        }

        let currentWidget = this.drilldownManager?.getActiveWidget?.() ?? this.widget;
        currentWidget?.repaintAllDrilldownLevels?.();
    }

    configureResizableDraggable() {
        let self = this;

        if (self.moveable || !self.dashboarWidgetElement) {
            if (self.moveable) {
                let widgets = self.dashboard.widgets().filter(x => x.id() !== self.id()).map(x => x.dashboarWidgetElement);
                self.moveable.elementGuidelines = widgets
            }

            return;
        }

        let dashboardWidgetElement = self.dashboarWidgetElement;
        let titleDiv = $(".dashboard-widget-title", dashboardWidgetElement)[0];
        let moveableDiv = $(".moveWidgetArea", dashboardWidgetElement)[0];
        let dragTarget = self.appearance.header.visible() ? titleDiv : moveableDiv;
        let dashboardElement = $(dashboardWidgetElement).parents(".dashboard-designer-resizer")[0];

        let layout = self.manualLayout;

        const draggable = true;
        const resizable = true;

        let widgets = self.dashboard.widgets().filter(x => x.id() !== self.id()).map(x => x.dashboarWidgetElement);

        // const scrollOptions = {
        //     container: () => self.dashboard.infiniteViewer.getContainer(),
        //     threshold: 20,
        //     getScrollPosition: () => {
        //         return [
        //             self.dashboard.infiniteViewer.getScrollLeft({ absolute: true }),
        //             self.dashboard.infiniteViewer.getScrollTop({ absolute: true }),
        //         ];
        //     }
        // };

        const scrollOptions = {
            container: () => self.dashboard.getScrollContainer()?.get?.(0),
            threshold: 100,
            getScrollPosition: () => {
                return [
                    self.dashboard.getScrollLeft(),
                    self.dashboard.getScrollTop(),
                ];
            }
        };

        self.moveable = new Moveable(dashboardElement, {
            dragTarget: dragTarget,
            target: dashboardWidgetElement,
            draggable: draggable,
            throttleDrag: 1,
            edgeDraggable: false,
            resizable: resizable,
            keepRatio: false,
            throttleScale: 0,

            //scrollable: true,
            //scrollOptions: scrollOptions,
            
            bounds: { left: 0, top: 0, right: 0, position: "css" },
            hideDefaultLines: true,
            linePadding: 4,
            edge: true,
            useResizeObserver: true,
            useMutationObserver: true,

            snappable: true,
            isDisplaySnapDigit: true,
            isDisplayInnerSnapDigit: false,
            snapGap: true,
            snapDirections: { "top": true, "left": true, "bottom": true, "right": true, "center": true, "middle": true },
            elementSnapDirections: { "top": true, "left": true, "bottom": true, "right": true, "center": true, "middle": true },
            snapThreshold: 5,
            elementGuidelines: widgets
        });

        //#infinite-scrollbar
        //self.moveable.on("scroll", ({ direction, target }) => {
        //    self.dashboard.scrollBy(direction[0] * 10, direction[1] * 10);
        //    const deltaY = direction[1] * 1;
        //    self.dashboard.scrollBy(0, deltaY);
        //    let top = parseInt(dashboardWidgetElement.style.top) + Math.trunc(deltaY);
        //    if (top < 0) { 
        //        top = 0;
        //    }
        //    target.style.top = `${top}px`;

        //    console.log("scroll event triggered ", direction[0], direction[1]);
        //});

        self.moveable.on("drag", e => {
            let left = parseInt(dashboardWidgetElement.style.left) + Math.trunc(e.delta[0]);
            e.target.style.left = `${left}px`;

            let top = parseInt(dashboardWidgetElement.style.top) + Math.trunc(e.delta[1]);
            e.target.style.top = `${top}px`;
        });

        self.moveable.on("resize", e => {

            if (e.direction[0] === -1) {
                let left = parseInt(dashboardWidgetElement.style.left) - Math.trunc(e.delta[0]);
                e.target.style.left = `${left}px`;
            }
            if (e.direction[1] === -1) {
                let top = parseInt(dashboardWidgetElement.style.top) - Math.trunc(e.delta[1]);
                e.target.style.top = `${top}px`;
            }

            let width = parseInt(dashboardWidgetElement.style.width) + Math.trunc(e.delta[0]);
            e.target.style.width = `${width}px`;

            let height = parseInt(dashboardWidgetElement.style.height) + Math.trunc(e.delta[1]);
            e.target.style.height = `${height}px`;
        });

        self.moveable.on("dragEnd", ({ target, isDrag }) => {
            if (isDrag) {
                let left = parseInt(dashboardWidgetElement.style.left);
                let top = parseInt(dashboardWidgetElement.style.top);

                layout.left(left);
                layout.top(top);
            }
        });

        self.moveable.on("resizeEnd", ({ target }) => {
            let left = parseInt(dashboardWidgetElement.style.left);
            let top = parseInt(dashboardWidgetElement.style.top);
            let width = parseInt(dashboardWidgetElement.style.width);
            let height = parseInt(dashboardWidgetElement.style.height);

            layout.left(left);
            layout.top(top);
            layout.width(width);
            layout.height(height);

            //Force report to redraw after resizing, apparently the other widgets don't need this
            let currentWidget = self.drilldownManager?.getActiveWidget?.() ?? self.widget;
            if (currentWidget && currentWidget.widgetType === WidgetType.Report) {
                currentWidget?.repaint?.();
            }
        });

        self.appearance.header.visible.subscribe(function (newValue: boolean) {
            dragTarget = newValue ? titleDiv : moveableDiv;
            self.moveable.dragTarget = dragTarget;
        });
    }

    private layoutValidation(valueKo: KnockoutObservable<number>, property: string) {
        return true;
    }

    private async setupSettings() {
        let self = this;

        self.createSettingItem(self.appearance.header.visible);
        self.createSettingItem(self.appearance.header.border);
        self.createSettingItem(self.appearance.header.borderColor);
        self.createSettingItem(self.appearance.header.backColor);
        self.createSettingItem(self.appearance.header.fontSize);
        self.createSettingItem(self.appearance.header.fontBold);
        self.createSettingItem(self.appearance.header.fontItalic);
        self.createSettingItem(self.appearance.header.fontColor);
        self.createSettingItem(self.appearance.header.fontAlignment);

        self.createSettingItem(self.appearance.border.visible);
        self.createSettingItem(self.appearance.border.shadow);

        self.createSettingItem(self.appearance.fontColor);
        self.createSettingItem(self.appearance.backgroundColor);

        self.createSettingItem(self.tabDetailId);
        self.createSettingItem(self.copyMetricsToTab);
        self.createSettingItem(self.copyDimensionsToTab);

        self.createSettingItem(self.manualLayout.height, function () { return self.layoutValidation(self.manualLayout.height, "height") });
        self.createSettingItem(self.manualLayout.left, function () { return self.layoutValidation(self.manualLayout.left, "left") });
        self.createSettingItem(self.manualLayout.top, function () { return self.layoutValidation(self.manualLayout.top, "top") });
        self.createSettingItem(self.manualLayout.width, function () { return self.layoutValidation(self.manualLayout.height, "width") });
    }

    private createSettingItem(property: KnockoutObservable<any>, validationFunction?: () => boolean) {
        let self = this;
        property?.subscribe(function (newValue) {
            if (!validationFunction || validationFunction()) {
                self.saveSettings();
            }
        });
    }

    private saveSettings() {
        let submitObject = this.getBaseSubmitObject();
        submitObject.layout = ko.toJSON(this.manualLayout);
        submitObject.tabDetailId = this.tabDetailId();
        submitObject.copyMetricsToTab = this.copyMetricsToTab();
        submitObject.copyDimensionsToTab = this.copyDimensionsToTab();
        //Need to remove dashboard property because of the circular reference (dashboard -> widget -> appearance -> dashboard)
        submitObject.appearance = JSON.stringify(this.appearance, function (key, value) {
            // Exclude the "dashboard" property
            if (key === 'dashboard') {
                return undefined;
            }
            return irGlobal.jsonReplacer(key, value);
        });

        let url = urlConfigs.dashboard + "SaveSettings";

        let successFunction = function() { };
        
        irGlobal.updateFromServer({},
            url,
            submitObject,
            successFunction,
            function (textStatus, errorThrown, errorItem: IError) {
                errorItem.userMessage = "Failed to save Widget settings";
                irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, errorThrown);
            },
            true, "POST", undefined, undefined, undefined, false);
    }

    updateName(name: string) {
        let self = this;
        let submitObject = this.dashboard.tab.getBaseSubmitObject() as any;
        submitObject.widgetId = this.id();
        submitObject.name = name;

        let o = {
            url: urlConfigs.dashboard + "UpdateWidgetName",
            data: submitObject,
            dataType: "json"
        } as RequestServerParams;
        o.successFunction = function (data, e) {
            if (self.drilldownManager.drilldownList().length > 0) {
                self.drilldownManager.drilldownViewList()[0].tabName(name);
            }

            self.name(name);
        };
        o.failureFunction = function (textStatus, failureText, errorItem: IError) {
            errorItem.userMessage = `Failed to update widget name`;
            irGlobal.defaultErrorFunction(errorItem.userMessage, textStatus, failureText);
        };

        irGlobal.requestServer(o);
    }
}